import { createWebHistory, createRouter } from "vue-router";
import registrationRoutes from "@/router/registrationRoutes";
import helpdeskRoutes from "@/router/helpdeskRoutes";
import configurationRoutes from "@/router/configurationRoutes";
import investigationRoutes from "@/router/investigationRoutes";
import workflowRoutes from "@/router/workflowRoutes";
import securityRoutes from "@/router/securityRoutes";
import routes from './routes';
import appConfig from "../../app.config";
import userManagementRoutes from "@/router/userManagementRoutes";
import supplierRoutes from "@/router/supplierRoutes";
import accountManagementRoutes from "@/router/accountManagementRoutes";
import procurementAuditRoutes from "@/router/procurementAuditRoutes";
import eLearningRoutes from "@/router/eLearningRoutes";
import systemRoutes from "@/router/systemRoutes";
import contractsRoutes from "@/router/contractsRoutes";
import biddingRoutes from "@/router/biddingRoutes";
import paymentsRoutes from "@/router/paymentsRoutes";
import appealsRoutes from "@/router/appealsRoutes";
import complianceMonitoringRoutes from "@/router/complianceMonitoringRoutes";
import { checkUserHasRequiredPrivilege } from "@/helpers/utils";
import annualProcurementPlansRoutes from "@/router/annualProcurementPlansRoutes";
import procurementReportingRoutes from "@/router/procurementReportingRoutes";
import procurementTrailRoutes from "@/router/procurementTrailRoutes";
import suspensionRoutes from "@/router/suspensionRoutes";
import paymentsReportingRoutes from "@/router/paymentsReportingRoutes";
import usageAnalyticsRoutes from "@/router/usageAnalyticsRoutes";
import { analyticsRoutes, ANALYTICS_MODULES as M } from '@/analytics/routeAnalytics';

// Merge all routes
const allRoutes = [
  ...analyticsRoutes(M.ACCOUNT_MANAGEMENT, accountManagementRoutes),
  ...analyticsRoutes(M.USER_MANAGEMENT, userManagementRoutes),
  ...analyticsRoutes(M.SUPPLIER_MANAGEMENT, supplierRoutes),
  ...analyticsRoutes(M.REGISTRATION, registrationRoutes),
  ...analyticsRoutes(M.HELP_DESK, helpdeskRoutes),
  ...analyticsRoutes(M.CONFIGURATION, configurationRoutes),
  ...analyticsRoutes(null, routes),
  ...analyticsRoutes(M.INVESTIGATIONS, investigationRoutes),
  ...analyticsRoutes(M.WORKFLOW, workflowRoutes),
  ...analyticsRoutes(M.PROCUREMENT_AUDIT, procurementAuditRoutes),
  ...analyticsRoutes(M.E_LEARNING, eLearningRoutes),
  ...analyticsRoutes(M.ADMINISTRATION, systemRoutes),
  ...analyticsRoutes(M.SECURITY, securityRoutes),
  ...analyticsRoutes(M.CONTRACT_MANAGEMENT, contractsRoutes),
  ...analyticsRoutes(M.BIDDING, biddingRoutes),
  ...analyticsRoutes(M.PAYMENTS, paymentsRoutes),
  ...analyticsRoutes(M.APPEALS, appealsRoutes),
  ...analyticsRoutes(M.PROCUREMENT_PLANNING, annualProcurementPlansRoutes),
  ...analyticsRoutes(M.REPORTING, procurementReportingRoutes),
  ...analyticsRoutes(M.PROCUREMENT_TRAIL, procurementTrailRoutes),
  ...analyticsRoutes(M.COMPLIANCE, complianceMonitoringRoutes),
  ...analyticsRoutes(M.SUSPENSIONS, suspensionRoutes),
  ...analyticsRoutes(M.REPORTING, paymentsReportingRoutes),
  ...analyticsRoutes(M.REPORTING, usageAnalyticsRoutes),
];
const router = createRouter({
  history: createWebHistory(),
  routes: allRoutes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

router.beforeEach(async (routeTo, routeFrom, next) => {
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
  const jwt = sessionStorage.getItem('jwt');

  if (authRequired && (!jwt || !jwt.length)) {
    return next('/login');
  }

  const privilegesRequired = routeTo.matched.reduce((privileges, route) => {
    if (route.meta.privileges) {
      privileges.push(...route.meta.privileges);
    }
    return privileges;
  }, []);

  if (privilegesRequired.length) {
    const hasRequiredPrivileges = checkUserHasRequiredPrivilege(privilegesRequired);

    if (!hasRequiredPrivileges) {
      return next({
        name: 'unauthorized',
        query: { redirectFrom: routeTo.fullPath }
      });
    }
  }

  return next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  try {
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            if (args.length) {
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          resolve();
        }
      });
    }
  } catch (error) {
    return;
  }

  document.title = (routeTo.meta.title || '') + ' | ' + appConfig.title;
  next();
});

export default router;
